import { Search } from '@mui/icons-material';
import { InputAdornment, TextField } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { useDebounce } from 'use-debounce';
import { toast } from 'react-toastify';
import { GripVertical, Trash2 } from 'lucide-react';

import { DndContext, DragOverlay, useSensor, useSensors, PointerSensor, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import BDHelmet from '@/components/BDHelmet';
import Pagination from '@/components/ui/Pagination/Pagination';
import Delete from '@/components/Modal/General/Delete';
import Loader from '@/components/ui/Loader/Loader';
import Group from '@/components/Modal/Project/Group';
import usePatch from '@/hooks/usePatch';

import project from '@/api/project';
import icAddNew from '@/assets/icons/add-new-user.png';

const { getProductGroup, updateGroupCategory } = project();

const updateGroup = async (id, data) => {
	return new Promise((resolve, reject) => {
		updateGroupCategory(id, data)
			.then((res) => {
				resolve(res);
			})
			.catch((err) => {
				reject(err);
			});
	});
};

const SortableRow = ({ row, children }) => {
	const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
		id: row.id.toString(),
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition: isDragging ? 'none' : 'transform 200ms ease-in-out',
		opacity: isDragging ? 0.5 : 1,
		background: isDragging ? '#f5f5f5' : 'white',
		cursor: isDragging ? 'grabbing' : 'grab',
	};

	return (
		<div ref={setNodeRef} style={style} {...attributes} {...listeners}>
			{children}
		</div>
	);
};

const SortableHandle = ({ id }) => {
	const { attributes, listeners, setNodeRef } = useSortable({ id: id.toString() });

	return (
		<div
			ref={setNodeRef}
			{...attributes}
			{...listeners}
			style={{ cursor: 'grab', padding: '5px' }}
			className='flex items-center justify-center w-full h-full'
		>
			<GripVertical size={16} style={{ opacity: 0.6 }} className='m-auto' />
		</div>
	);
};

export default function Groups() {
	const [s, setSearch] = useState('');
	const [search] = useDebounce(s, 300);
	const [newGroup, setNewGroup] = useState(false);
	const [selectedRow, setSelectedRow] = useState(false);
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
	const [isDeleteGroup, setIsDeleteGroup] = useState(false);
	const [orderedData, setOrderedData] = useState([]);
	const [activeRow, setActiveRow] = useState(null);

	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

	const handleDragEnd = async (event) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;

		const oldIndex = orderedData.findIndex((item) => item.id === +active.id);
		const newIndex = orderedData.findIndex((item) => item.id === +over.id);

		const item = oldIndex >= 0 ? orderedData[oldIndex] : null;

		if (oldIndex !== -1 && newIndex !== -1) {
			const reordered = arrayMove(orderedData, oldIndex, newIndex);
			setOrderedData(reordered);
		}
		const objUpdate = {
			...item,
			index: newIndex,
		};
		try {
			await updateGroup(item.id, objUpdate);
			toast.success('Order updated!');
		} catch (e) {
			toast.error('Failed to update order');
		}
	};

	const oC = useRef(null);

	const objFilters = useMemo(() => {
		const obj = { page_size: paginationModel.pageSize, order_by: 'index' };
		if (search) {
			obj.search = search;
			obj.max_size = true;
		}
		return obj;
	}, [search, paginationModel]);

	const {
		data: d,
		isFetching,
		isFetched,
		refetch,
	} = useQuery({
		queryKey: ['groups', paginationModel, search],
		queryFn: () => {
			const obj = objFilters;
			if (!search && paginationModel.page) obj.page = paginationModel.page + 1;
			if (paginationModel.page === 0 && 'page' in obj) delete obj.page;
			return getProductGroup(obj);
		},
		placeholderData: keepPreviousData,
		refetchOnWindowFocus: false,
	});

	const dataCount =
		useMemo(() => {
			const count = d?.data?.count || d?.data?.length;
			if (isFetched && count) {
				oC.current = count;
				return count;
			}
		}, [isFetched, d, oC]) ||
		oC.current ||
		0;
	// const dataList = useMemo(() => d?.data?.results || d?.data || [], [d]) || [];
	const extractDataList = (d) => d?.data?.results || d?.data || [];

	useEffect(() => {
		if (d) {
			setOrderedData(extractDataList(d));
		}
	}, [d]);

	const {
		mutate: deleteCategory,
		isPending,
		isSuccess,
	} = usePatch({
		url: `groupcategory/${isDeleteGroup?.id}`,
		method: 'delete',
		callback: () => {
			toast.success(`kategori deleted`);
			refetch();
			setIsDeleteGroup(false);
		},
		onError: () => {},
		showError: true,
	});

	const handleDelete = () => {
		if (!isDeleteGroup) return;
		deleteCategory();
	};

	const columns = [
		{
			field: 'drag',
			headerName: '',
			width: 50,
			sortable: false,
			disableColumnMenu: true,
			renderCell: (params) => <SortableHandle id={params.row.id} />,
		},
		{
			field: 'groupname',
			headerName: 'Navn',
			headerAlign: 'center',
			align: 'left',
			flex: 1,
		},
		{
			field: 'groupnumber',
			headerName: 'Nummer',
			headerAlign: 'center',
			align: 'left',
			flex: 1,
		},
		{
			field: 'time_update',
			headerName: 'Oppdatert dato',
			headerAlign: 'center',
			align: 'center',
			width: 144,
			renderCell: (params) => {
				const { time_update } = params.row;
				return (
					<div className='flex flex-col'>
						<span>{format(new Date(time_update), 'yyyy/MM/dd')}</span>
					</div>
				);
			},
		},
		{
			field: 'status',
			headerName: '',
			headerAlign: 'center',
			align: 'center',
			width: 144,
			renderCell: (params) => {
				const { row } = params;
				return (
					<button aria-label='toggle password visibility' onClick={() => setIsDeleteGroup({ id: row.id, name: row.groupname })}>
						<Trash2 size={16} />
					</button>
				);
			},
		},
	];

	const onSaveGroup = () => {
		refetch();
	};

	return (
		<>
			<BDHelmet title='Groups' description='BDS Samferdsel project groups' />
			<div className='container flex flex-col gap-4 pt-8 pb-8 mx-auto'>
				<div className='flex flex-col gap-4'>
					<div className='flex justify-between gap-4'>
						<TextField
							id='search'
							className='w-full input-search'
							placeholder='Søk'
							onChange={(e) => {
								setSearch(e.target.value);
							}}
							size='small'
							InputProps={{
								startAdornment: (
									<InputAdornment position='start'>
										<Search style={{ color: 'black' }} />
									</InputAdornment>
								),
							}}
						/>
						<button
							onClick={() => setNewGroup(true)}
							title='Registrer ny bruker'
							className='relative flex items-center justify-center gap-2 min-w-[140px] h-10 btn'
						>
							<div className='flex items-center justify-center w-5 h-5 bg-white rounded-full'>
								<img src={icAddNew} />
							</div>
							Ny Gruppe
						</button>
					</div>
					<div>
						<span className='font-bold'>Totalt </span> <span className='text-bd-blue'>{dataCount}</span>{' '}
						<span className='text-bd-gLight'>brukere</span>
					</div>
					<div className='mx-auto'>
						{isPending && !isSuccess && <Loader />}

						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragStart={({ active }) => setActiveRow(active.id)}
							onDragEnd={(event) => {
								handleDragEnd(event);
								setActiveRow(null);
							}}
						>
							<SortableContext items={orderedData.map((item) => item.id)} strategy={verticalListSortingStrategy}>
								<DataGrid
									autoHeight
									rows={orderedData}
									loading={isFetching}
									columns={columns}
									getRowId={(row) => row.id}
									// rowsPerPageOptions={[10, 20, 50]}
									className='h-full mx-auto mt-0 w-[780px]'
									disableColumnFilter
									disableColumnSelector
									disableDensitySelector
									disableToolbarButton
									disableRowSelectionOnClick
									processRowUpdate={(newRow) => {
										// Manually update state to ensure sorting applies
										setOrderedData((prev) => prev.map((row) => (row.id === newRow.id ? newRow : row)));
										return newRow;
									}}
									slots={{
										pagination: Pagination,
									}}
									getRowClassName={(params) => {
										let cellClass = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
										cellClass += ' cell-fix';
										if (selectedRow?.id === params.row.id) {
											cellClass += ' font-bold border border-black';
										}
										return cellClass;
									}}
									slotProps={{
										loadingOverlay: {
											variant: 'skeleton',
											noRowsVariant: 'skeleton',
										},
										pagination: {
											page: paginationModel.page,
											pageSize: paginationModel.pageSize,
											rowCount: dataCount,
										},
									}}
									// onCellClick={(params, event) => {
									// 	if (event.target.type === 'checkbox' || ['svg', 'path'].includes(event.target.tagName)) return;
									// 	setSelectedRow(params.row);
									// }}
									pageSizeOptions={[10, 20, 50, 100]}
									initialState={{
										pagination: { paginationModel: { page: 0, pageSize: 10 } },
									}}
									rowCount={dataCount}
									paginationModel={paginationModel}
									paginationMode='server'
									onPaginationModelChange={(obj) => setPaginationModel(obj)}
									onRowClick={(params) => {
										if (event.target.type === 'checkbox' || ['svg', 'path'].includes(event.target.tagName)) return;
										setSelectedRow(params.row);
									}}
									components={{
										Row: (props) => <SortableRow row={props.row}>{props.children}</SortableRow>,
									}}
								/>
							</SortableContext>
							<DragOverlay>
								{activeRow ? (
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											width: '500px', // Adjust to match your DataGrid row
											padding: '10px',
											background: '#fff',
											border: '1px solid #ccc',
											boxShadow: '0px 2px 5px rgba(0,0,0,0.2)',
											borderRadius: '4px',
										}}
									>
										{/* Drag Handle Icon */}
										<GripVertical size={16} style={{ opacity: 0.6, marginRight: '10px' }} />

										{/* Navn (Name) */}
										<span style={{ flex: 1, fontWeight: 'bold' }}>
											{orderedData.find((item) => item.id === +activeRow)?.groupname || ''}
										</span>

										{/* Categorys (Category) */}
										<span style={{ flex: 1, color: '#666' }}>{orderedData.find((item) => item.id === +activeRow)?.groupnumber || '-'}</span>
									</div>
								) : null}
							</DragOverlay>
						</DndContext>
					</div>
				</div>
				{!!selectedRow && <Group open={!!selectedRow} onSave={onSaveGroup} data={selectedRow} onClose={() => setSelectedRow(false)} />}
				{!!newGroup && <Group open={!!newGroup} onSave={onSaveGroup} onClose={() => setNewGroup(false)} />}
				{!!isDeleteGroup && (
					<Delete open={!!isDeleteGroup} data={isDeleteGroup} onSave={handleDelete} onClose={() => setIsDeleteGroup(false)} />
				)}
			</div>
		</>
	);
}
